import React, { useMemo, useState } from 'react'
import { useAppSelector } from '../../../../../../../../../../app/hooks'
import { AttachmentDataWithRequiredAttachment, AttachmentType } from '../../../../../../../../../../@types/types'
import { PictureCarousel } from './PictureCarousel'
import { WebViewPictureThumbnailStrip } from './WebViewPictureThumbnailStrip'
import { useMultimediaDetailsModalContext } from '../../../../multimediaDetailsModalContext/multimediaModalsContext'
import { createAndTriggerPluploadFileInput } from '../../../utils/fileUploadUtils'
import styles from './MobileMultimediaPreview.module.css'
import { BulkEditMultimediaModal } from './BulkEditMultimediaModal'

interface MobileMultimediaPreviewProps {
  dropzoneId: string
  pictures: AttachmentDataWithRequiredAttachment[]
  isInlinePicturesEnabled?: boolean
  onPictureDeleted?: (pictureId: string) => void
}

export const WebViewMultimediaPreview: React.FC<MobileMultimediaPreviewProps> = ({
  dropzoneId,
  pictures,
  isInlinePicturesEnabled = false,
  onPictureDeleted,
}) => {
  const attachments = useAppSelector((state) => state.multimediaFeature.attachments) ?? []
  const { openModalForEditClick, onAttachmentTitleChange, onAttachmentDelete } = useMultimediaDetailsModalContext()
  const [selectedPictureId, setSelectedPictureId] = useState<string>()
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false)
  const [bulkEditPictures, setBulkEditPictures] = useState<AttachmentDataWithRequiredAttachment[]>(pictures)
  const [updatedTitles, setUpdatedTitles] = useState<Record<string, string>>({})
  const previewPictures = useMemo(
    () =>
      pictures.map((picture) => {
        const title = updatedTitles[picture.attachment.id]
        return title === undefined ? picture : { ...picture, attachment: { ...picture.attachment, title } }
      }),
    [pictures, updatedTitles]
  )
  const selectedPicture = previewPictures.find((item) => item.attachment.id === selectedPictureId) ?? previewPictures[0]

  if (!selectedPicture) {
    return null
  }

  const handleEditClick = (picture: AttachmentDataWithRequiredAttachment) => {
    if (isInlinePicturesEnabled) {
      setSelectedPictureId(picture.attachment.id)
      setBulkEditPictures(previewPictures)
      setIsBulkEditModalOpen(true)
      return
    }

    const modalAttachments = attachments.filter(
      (item) => item.attachmentType === AttachmentType.PICTURE || item.attachmentType === AttachmentType.DOCUMENT
    )
    openModalForEditClick(modalAttachments.indexOf(picture))
  }

  const handleBulkTitleChange = (attachment: AttachmentDataWithRequiredAttachment) => {
    setUpdatedTitles((titles) => ({
      ...titles,
      [attachment.attachment.id]: attachment.attachment.title ?? '',
    }))
    onAttachmentTitleChange(attachment)
  }

  const handleDeleteClick = async (picture: AttachmentDataWithRequiredAttachment) => {
    const deletedIndex = previewPictures.findIndex((item) => item.attachment.id === picture.attachment.id)
    const nextSelectedPicture = previewPictures[deletedIndex + 1] ?? previewPictures[deletedIndex - 1]
    await onAttachmentDelete({
      ...picture.attachment,
      size: picture.file?.size ?? picture.attachment.size,
    })
    onPictureDeleted?.(picture.attachment.id)
    setSelectedPictureId(nextSelectedPicture?.attachment.id)
  }

  return (
    <>
      <div className={`relative w-full ${styles.previewContainer}`}>
        <PictureCarousel
          attachments={previewPictures}
          selectedPictureId={selectedPicture.attachment.id}
          onSelect={setSelectedPictureId}
          onEditClick={handleEditClick}
          onDeleteClick={isInlinePicturesEnabled ? handleDeleteClick : undefined}
          onAddClick={createAndTriggerPluploadFileInput}
          dropzoneId={dropzoneId}
        />
      </div>
      {isInlinePicturesEnabled && (
        <WebViewPictureThumbnailStrip
          attachments={previewPictures}
          selectedPictureId={selectedPicture.attachment.id}
          onSelect={setSelectedPictureId}
          onAddClick={createAndTriggerPluploadFileInput}
        />
      )}
      <BulkEditMultimediaModal
        attachments={bulkEditPictures}
        initialAttachmentIndex={bulkEditPictures.findIndex(
          (picture) => picture.attachment.id === selectedPicture.attachment.id
        )}
        isOpen={isBulkEditModalOpen}
        closeModal={() => setIsBulkEditModalOpen(false)}
        onTitleChange={handleBulkTitleChange}
      />
    </>
  )
}
